import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PerksCard from '../../Components/PerksCard';

import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import InfoSection from '../../../../global/InfoSection';
import LoadingBar from '../../../../global/LoadingBar';
import { getGenericError } from '../../../../utils/errors';
import useTbbPaymentConfig from '../../../../../../hooks/useTbbPaymentConfig';
import { TBB_PARTNER_TAG, TBB_PAYMENT_CONFIG_ITEMS } from '../../../../utils/tbbRegistration';
import StripeCustomCheckout from '../../../../global/StripeElements/StripeCustomCheckout';
import CouponCodeInput from '../../../../global/CouponCodeInput';
import {
  createPayOnRegCheckoutSession,
  createSubscriptionCheckoutSession,
} from '../../../../utils/service';
import { REGISTER_FROM_CHECKOUT_PARAM } from '../../../../config/routes';
import { formatCurrencyNumber } from '../../../../utils/numbers';
import useSubscriptionPlans from '../../../../../../hooks/useSubscriptionPlans';
import tbbPinkBag from '../../../../images/tbb-pink-front.png';
import unlimitedBag from '../../../../images/unlimited_payonreg.png';

import './Payment.scss';

const PAY_ON_SHIP_OPTIONS = {
  FREE_BAG: 'free-bag',
  UNLIMITED: 'unlimited',
};

const Payment = ({
  code,
  payOnShipFee,
  storeConfig,
  isPayOnShip = false,
  isLoadingPayOnShipConfig = false,
  payOnShipConfig = {},
}) => {
  const { search } = useLocation();
  const { isSignedIn, isLoading: isLoadingFirebase } = useFirebaseAuth();
  const { isLoading: isLoadingTbbPaymentConfig, tbbPaymentConfig } = useTbbPaymentConfig();
  const {
    isLoading: isLoadingPlans, planValue, planPrevValue, planInterval,
  } = useSubscriptionPlans({ enabled: isPayOnShip });

  const [loadingStripe, setLoadingStripe] = useState(true);
  const [clientSecret, setClientSecret] = useState(null);
  const [errorStripe, setErrorStripe] = useState(null);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponErrorMessage, setCouponErrorMessage] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [discountedValue, setDiscountedValue] = useState(null);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [selectedOption, setSelectedOption] = useState(PAY_ON_SHIP_OPTIONS.FREE_BAG);
  const selectedOptionRef = useRef(selectedOption);
  selectedOptionRef.current = selectedOption;

  const fetchRequestId = useRef(0);

  const fetchClientSecret = useCallback(async (appliedCouponId = null, productType = null) => {
    fetchRequestId.current += 1;
    const requestId = fetchRequestId.current;

    setLoadingStripe(true);
    setCouponMessage('');
    setCouponErrorMessage('');

    const isUnlimited = productType === PAY_ON_SHIP_OPTIONS.UNLIMITED;

    let subscriptionSearchParams;
    if (isUnlimited) {
      subscriptionSearchParams = new URLSearchParams(search);
      subscriptionSearchParams.set(REGISTER_FROM_CHECKOUT_PARAM, 'true');
    }
    const payOnRegSearchParams = new URLSearchParams(search);

    const { data, error: checkoutSessionError } = isUnlimited
      ? await createSubscriptionCheckoutSession({
        couponId: appliedCouponId,
        isDirectPayment: true,
        searchParams: subscriptionSearchParams,
      })
      : await createPayOnRegCheckoutSession(
        code,
        appliedCouponId,
        productType,
        payOnRegSearchParams,
      );

    if (requestId !== fetchRequestId.current) {
      return;
    }

    if (checkoutSessionError || !data?.clientSecret) {
      setErrorStripe(checkoutSessionError?.message || getGenericError());
      setClientSecret(null);
      setLoadingStripe(false);
      return;
    }

    const appliedCoupon = Boolean(appliedCouponId && data.couponApplied);
    setIsCouponApplied(appliedCoupon);
    setDiscountedValue(data.discountedValue);
    setClientSecret(data.clientSecret);
    setErrorStripe(null);
    setLoadingStripe(false);

    if (appliedCouponId) {
      if (appliedCoupon) {
        setCouponMessage('Coupon applied successfully');
        setCouponErrorMessage('');
      } else {
        setCouponMessage('');
        setCouponErrorMessage('Invalid coupon');
      }
    }
    setCouponCode('');
  }, [code, search]);

  const handleApplyCode = useCallback(async () => {
    await fetchClientSecret(
      couponCode,
      isPayOnShip ? selectedOption : null,
    );
  }, [couponCode, fetchClientSecret, isPayOnShip, selectedOption]);

  const handleSelectOption = useCallback((option) => {
    if (option === selectedOption) return;
    setSelectedOption(option);
    setIsCouponApplied(false);
    setDiscountedValue(null);
    setCouponMessage('');
    setCouponErrorMessage('');
    fetchClientSecret(null, option);
  }, [selectedOption, fetchClientSecret]);

  const items = useMemo(
    () => {
      const recyclingItem = tbbPaymentConfig.items?.find(
        item => item.id === TBB_PAYMENT_CONFIG_ITEMS.recycling,
      );

      if (!recyclingItem) {
        return null;
      }

      const title = recyclingItem.titlePayment?.replace(TBB_PARTNER_TAG, storeConfig?.title);

      const perksItems = recyclingItem.children
        ?.map((child) => ({
          key: child.id,
          title: child.titlePayment,
        })) || [];

      return {
        key: recyclingItem.id,
        title,
        children: perksItems,
      };
    },
    [tbbPaymentConfig.items, storeConfig?.title],
  );

  const checkoutFullValue = useMemo(() => {
    if (isPayOnShip && selectedOption === PAY_ON_SHIP_OPTIONS.UNLIMITED) {
      return planValue ?? payOnShipFee;
    }
    return payOnShipFee;
  }, [isPayOnShip, selectedOption, planValue, payOnShipFee]);

  const isPlanUnavailable = isPayOnShip && !isLoadingPlans && planValue == null;

  const isLoadingInfo = (
    isLoadingFirebase
    || isLoadingTbbPaymentConfig
    || (isPayOnShip && isLoadingPayOnShipConfig)
    || (isPayOnShip && isLoadingPlans)
  );
  const isLoadingPayment = isLoadingFirebase || loadingStripe;

  useEffect(() => {
    if (!isLoadingFirebase && isSignedIn) {
      fetchClientSecret(null, isPayOnShip ? selectedOptionRef.current : null);
    }
  }, [isLoadingFirebase, isSignedIn, fetchClientSecret, isPayOnShip]);

  return (
    <div className={`RegisterPayment ${isPayOnShip ? 'RegisterPayment--payOnShip' : ''}`}>
      <InfoSection titleText="Payment" titleStyles="textTrasformNone" />

      {isPayOnShip && isLoadingInfo && <LoadingBar />}

      {isPayOnShip && !isLoadingInfo && (
        <>
          {/* Free Bag Option */}
          <div
            className={`RegisterPayment__option ${selectedOption === PAY_ON_SHIP_OPTIONS.FREE_BAG ? 'RegisterPayment__option--selected' : ''}`}
            onClick={() => handleSelectOption(PAY_ON_SHIP_OPTIONS.FREE_BAG)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleSelectOption(PAY_ON_SHIP_OPTIONS.FREE_BAG)}
          >
            <div className="RegisterPayment__option-checkbox">
              {selectedOption === PAY_ON_SHIP_OPTIONS.FREE_BAG && <div className="RegisterPayment__option-checkmark" />}
            </div>
            <div className="RegisterPayment__option-content">
              <div className="RegisterPayment__option-title">1 FREE BAG</div>
              <div className="RegisterPayment__option-description">
                {`Includes pre-paid shipping label and ${payOnShipConfig.PAY_ON_SHIP_REWARDS_POINTS} TrashieCash rewards points.`}
              </div>
            </div>
            <div className="RegisterPayment__option-image">
              <img src={tbbPinkBag} alt="Take Back Bag" />
            </div>
          </div>

          <div className="RegisterPayment__option-price">
            {formatCurrencyNumber(payOnShipFee)}
            {' '}
            Value
          </div>

          {/* OR Separator */}
          <div className="RegisterPayment__separator">
            <div className="RegisterPayment__separator-line" />
            <span className="RegisterPayment__separator-text">OR</span>
            <div className="RegisterPayment__separator-line" />
          </div>

          {/* Unlimited Option */}
          <div
            className={`RegisterPayment__option ${selectedOption === PAY_ON_SHIP_OPTIONS.UNLIMITED ? 'RegisterPayment__option--selected' : ''} ${isPlanUnavailable ? 'RegisterPayment__option--disabled' : ''}`}
            onClick={() => !isPlanUnavailable && handleSelectOption(PAY_ON_SHIP_OPTIONS.UNLIMITED)}
            role="button"
            tabIndex={isPlanUnavailable ? -1 : 0}
            onKeyDown={(e) => e.key === 'Enter' && !isPlanUnavailable && handleSelectOption(PAY_ON_SHIP_OPTIONS.UNLIMITED)}
            aria-disabled={isPlanUnavailable}
          >
            <div className="RegisterPayment__option-checkbox">
              {selectedOption === PAY_ON_SHIP_OPTIONS.UNLIMITED && <div className="RegisterPayment__option-checkmark" />}
            </div>
            <div className="RegisterPayment__option-content">
              <div className="RegisterPayment__option-title">TRASHIE UNLIMITED</div>
              <div className="RegisterPayment__option-description">
                Get unlimited bags, boxes, and 2X points for one price all year long.
              </div>
            </div>
            <div className="RegisterPayment__option-image RegisterPayment__option-image--full">
              <img src={unlimitedBag} alt="Unlimited" />
            </div>
          </div>

          {planValue != null && planInterval ? (
            <div className="RegisterPayment__option-price">
              {planPrevValue > 0 && (
                <span className="RegisterPayment__option-price--prev">
                  {formatCurrencyNumber(planPrevValue)}
                </span>
              )}
              {' '}
              {formatCurrencyNumber(planValue)}
              /
              {planInterval}
            </div>
          ) : (
            <div className="RegisterPayment__option-price">
              {isLoadingPlans ? 'Loading...' : 'Unavailable'}
            </div>
          )}

        </>
      )}

      {!isPayOnShip && (
        isLoadingInfo ? (
          <LoadingBar />
        ) : (
          <PerksCard
            payOnShipFee={payOnShipFee}
            items={items.children}
            title={items.title}
          />
        )
      )}

      <CouponCodeInput
        value={couponCode}
        onChange={setCouponCode}
        onApply={handleApplyCode}
        disabled={isLoadingPayment}
        applyDisabled={!couponCode || isLoadingPayment}
        successMessage={couponMessage}
        errorMessage={couponErrorMessage}
      />

      {!isLoadingPayment && (
        errorStripe ? (
          <div className="error">{errorStripe}</div>
        ) : (
          <StripeCustomCheckout
            clientSecret={clientSecret}
            fullValue={checkoutFullValue}
            discountedValue={discountedValue}
            isCouponApplied={isCouponApplied}
          />
        )
      )}
    </div>
  );
};

export default Payment;
